import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, Mail, MessageCircle, Megaphone, Send, Users } from "lucide-react";

const stamp = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "—");

const Figure = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-2xl border border-border p-4" data-testid={testid}>
    <Icon size={15} className="text-secondary" />
    <div className="font-display text-3xl mt-2">{value}</div>
    <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
  </div>
);

/**
 * Admin → Broadcast: one message to the popup subscriber list, by email to everyone
 * and by WhatsApp to those who left a number. The draft is pre-filled from whichever
 * offer is live, and nothing is sent until the confirmation is accepted.
 */
export const BroadcastTab = () => {
  const [info, setInfo] = useState(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [useEmail, setUseEmail] = useState(true);
  const [useWhatsapp, setUseWhatsapp] = useState(true);
  const [confirm, setConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = () => api.get("/admin/marketing/broadcasts")
    .then(({ data }) => setHistory(data)).catch(() => {});

  useEffect(() => {
    api.get("/admin/marketing/broadcast/preview")
      .then(({ data }) => {
        setInfo(data);
        setSubject(data.default_subject);
        setMessage(data.default_message);
        setUseWhatsapp(data.whatsapp_available);
      })
      .catch(() => {});
    loadHistory();
  }, []);

  if (!info) return <Card className="border-border"><CardContent className="p-8 text-sm text-muted-foreground">Loading the list…</CardContent></Card>;

  const emailCount = useEmail ? info.recipients.email : 0;
  const waCount = useWhatsapp && info.whatsapp_available ? info.recipients.whatsapp : 0;

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/marketing/broadcast", {
        subject, message, send_email: useEmail, send_whatsapp: useWhatsapp,
      });
      toast.success(`Sent — ${data.email.sent} email(s), ${data.whatsapp.sent} WhatsApp message(s)`);
      if (data.email.failed || data.whatsapp.failed) {
        toast.error(`${data.email.failed + data.whatsapp.failed} message(s) could not be delivered`);
      }
      setConfirm(false);
      loadHistory();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSending(false); }
  };

  return (
    <Card className="border-border" data-testid="broadcast-tab">
      <CardContent className="p-6 lg:p-8">
        <h3 className="font-display text-2xl flex items-center gap-2"><Megaphone size={18} /> Broadcast</h3>
        <p className="text-sm text-muted-foreground mt-1 mb-6">
          One message to everyone who left their details in the welcome popup.
        </p>

        <div className="grid grid-cols-3 gap-4 max-w-lg">
          <Figure icon={Users} label="Subscribers" value={info.subscribers} testid="broadcast-total" />
          <Figure icon={Mail} label="Email only" value={info.subscribers - info.recipients.whatsapp} testid="broadcast-email-only" />
          <Figure icon={MessageCircle} label="With phone" value={info.recipients.whatsapp} testid="broadcast-with-phone" />
        </div>

        {!info.whatsapp_available && (
          <div className="flex items-start gap-3 mt-6 rounded-2xl border border-secondary/40 bg-secondary/10 p-4" data-testid="broadcast-whatsapp-warning">
            <AlertTriangle size={16} className="text-secondary mt-0.5 shrink-0" />
            <p className="text-sm text-muted-foreground">{info.whatsapp_note}</p>
          </div>
        )}

        <div className="mt-8 max-w-2xl space-y-4">
          <div>
            <Label className="overline">Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-2 bg-transparent" data-testid="broadcast-subject" />
          </div>
          <div>
            <Label className="overline">Message</Label>
            <Textarea rows={7} value={message} onChange={(e) => setMessage(e.target.value)} className="mt-2 bg-transparent" data-testid="broadcast-message" />
            <p className="text-xs text-muted-foreground mt-1.5">
              {info.promo
                ? `Pre-filled from the live offer (${info.promo.code}). Edit freely before sending.`
                : "No offer is live right now, so the draft is a plain note."}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-3 text-sm">
              <Switch checked={useEmail} onCheckedChange={setUseEmail} data-testid="broadcast-toggle-email" />
              Email ({info.recipients.email})
            </label>
            <label className="flex items-center gap-3 text-sm">
              <Switch
                checked={useWhatsapp && info.whatsapp_available}
                disabled={!info.whatsapp_available}
                onCheckedChange={setUseWhatsapp}
                data-testid="broadcast-toggle-whatsapp"
              />
              WhatsApp ({info.recipients.whatsapp}){!info.whatsapp_available && " — unavailable"}
            </label>
          </div>
          <Button
            onClick={() => setConfirm(true)}
            disabled={!message.trim() || (emailCount + waCount === 0)}
            className="btn-action"
            data-testid="broadcast-send"
          >
            <Send size={14} className="mr-1.5" /> Review and send
          </Button>
        </div>

        <Dialog open={confirm} onOpenChange={setConfirm}>
          <DialogContent className="max-w-md" data-testid="broadcast-confirm">
            <DialogHeader><DialogTitle className="font-display text-2xl">Send this broadcast?</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <p className="text-muted-foreground">It will go out straight away to:</p>
              <ul className="space-y-1.5">
                <li className="flex items-center gap-2" data-testid="broadcast-confirm-email">
                  <Mail size={14} className="text-secondary" /> {emailCount} subscriber(s) by email
                </li>
                <li className="flex items-center gap-2" data-testid="broadcast-confirm-whatsapp">
                  <MessageCircle size={14} className="text-secondary" />
                  {waCount} subscriber(s) by WhatsApp{!info.whatsapp_available && " (unavailable — skipped)"}
                </li>
              </ul>
              <p className="rounded-xl border border-border p-3 text-xs text-muted-foreground whitespace-pre-line">
                <b>{subject}</b>{"\n"}{message}
              </p>
            </div>
            <div className="flex gap-3 mt-2">
              <Button onClick={send} disabled={sending} className="btn-action flex-1" data-testid="broadcast-confirm-send">
                {sending ? "Sending…" : "Yes, send now"}
              </Button>
              <Button variant="outline" onClick={() => setConfirm(false)} className="btn-pill border-border">Cancel</Button>
            </div>
          </DialogContent>
        </Dialog>

        <div className="mt-10">
          <h4 className="font-display text-xl mb-3">Past broadcasts</h4>
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="broadcast-history-empty">Nothing sent yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="broadcast-history">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="py-3 pr-4 font-normal">Sent</th>
                    <th className="py-3 pr-4 font-normal">Subject</th>
                    <th className="py-3 pr-4 font-normal">Email</th>
                    <th className="py-3 font-normal">WhatsApp</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((b) => (
                    <tr key={b.id} className="border-t border-border/70" data-testid={`broadcast-row-${b.id}`}>
                      <td className="py-3 pr-4 whitespace-nowrap">{stamp(b.created_at)}</td>
                      <td className="py-3 pr-4">{b.subject || "—"}</td>
                      <td className="py-3 pr-4">
                        {b.email.skipped ? "skipped" : `${b.email.sent} sent${b.email.failed ? `, ${b.email.failed} failed` : ""}`}
                      </td>
                      <td className="py-3">
                        {b.whatsapp.skipped
                          ? (b.whatsapp_unavailable ? "unavailable" : "skipped")
                          : `${b.whatsapp.sent} sent${b.whatsapp.failed ? `, ${b.whatsapp.failed} failed` : ""}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default BroadcastTab;
